/**
 * Lalvin — Lallemand Oenology's wine, cider and mead line.
 *
 * Sibling brand to LalBrew (`dry.ts`), kept apart because a brewer filtering by
 * lab wants the beer strains and the wine strains in separate lists. Every
 * figure here came off Lallemand's own pages during this session: the product
 * page under `lallemandwine.com/en/<market>/products/wine-yeasts/…` for the
 * temperature band and the alcohol tolerance, and the maker-hosted technical
 * data sheet on `products.lallemandwine.com` for the dosage rate and the
 * character notes. The US market carries all but two; `K1-V1116` is only
 * published under the eastern-countries market and `Rhône 2056` under
 * Australia, so those two link there.
 *
 * **Every entry is `representative`, and for one reason:** an oenology sheet
 * never prints apparent attenuation. These are strains sold on the promise of
 * fermenting a must to dryness, so they carry a 95–100 % band, which is what a
 * dry finish implies and not a number Lallemand published. Flocculation is the
 * same story — the sheets talk about foam formation, not settling. Where
 * Lallemand does describe the sediment the word is used (EC-1118 "flocculates
 * well with very compact lees" → `high`; ICV D47 "yeast lees sediments well,
 * forming a compact layer" → `medium-high`); everywhere else the entry carries
 * a `medium` placeholder rather than a guess dressed up as a fact.
 *
 * Temperature and alcohol tolerance are taken from the product page, which is
 * the page linked. DV10 is the one place the two documents disagree — the page
 * says 17 % and the data sheet says 18 % — and the lower, linked figure wins.
 *
 * `sta1` is `null` throughout: Lallemand publishes no diastatic result for the
 * oenology range.
 *
 * Uvaferm 43 / 43 Restart is deliberately absent. It is a real Lallemand wine
 * strain and it is on the shelf, but Lallemand brands it Uvaferm on every
 * market page and in its own US e-shop, never Lalvin, so filing it under
 * `lab: 'Lalvin'` would be a link the data does not support.
 */

import type { Yeast } from '../../schema/yeast';

/** Lalvin wine, cider and mead strains. @see Yeast */
export const lalvinYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // The high-tolerance workhorses — bottle conditioning, mead, seltzer
  // -------------------------------------------------------------------------
  {
    id: 'lalvin-ec-1118',
    lab: 'Lalvin',
    productCode: 'EC-1118',
    name: 'Lalvin EC-1118 Prise de Mousse',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 30 },
    flocculation: 'high',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'The universal strain: a Champagne-region bayanus that will reach 18 % ABV anywhere from 10 to 30 °C, contributes almost no flavor of its own and drops a very compact cake. The default for bottle-conditioning a barleywine, restarting a stall, and for cider, mead and seltzer that should taste of the fruit. On thin nutrient it makes enough SO2 to inhibit a malolactic culture.',
    tags: [
      'wine',
      'champagne',
      'sparkling',
      'cider',
      'mead',
      'seltzer',
      'bottle-conditioning',
      'high-gravity',
      'restart',
      'neutral',
      'prise-de-mousse',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-ec1118-prise-de-mousse/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-k1-v1116',
    lab: 'Lalvin',
    productCode: 'K1-V1116',
    name: 'Lalvin V1116 K1 (ICV/INRAE)',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      "One of Lallemand's biggest ester producers and the strain that keeps going where others quit: 10–35 °C, 18 % ABV, low nutrient demand and no objection to a low-turbidity juice. Lallemand recommend it for whites, cider and ice wine, which is exactly the envelope a mead or a hard seltzer sits in.",
    tags: [
      'wine',
      'cider',
      'mead',
      'seltzer',
      'high-gravity',
      'esters',
      'fruity',
      'cold-tolerant',
      'k1',
    ],
    url: 'https://www.lallemandwine.com/en/eastern-countries/products/wine-yeasts/lalvin-v1116/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-dv10',
    lab: 'Lalvin',
    productCode: 'DV10',
    name: 'Lalvin DV10',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 35 },
    flocculation: 'medium',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'An Epernay bayanus selected for the second fermentation in the bottle: it starts at pH 2.9 under 50 mg/L total SO2 at 10 °C and still finishes. Low foaming, clean, and one of the strains Lallemand names on its own cider pages. The one to reach for when a strong beer or a cider has to carbonate in package.',
    tags: [
      'wine',
      'champagne',
      'sparkling',
      'cider',
      'mead',
      'seltzer',
      'bottle-conditioning',
      'high-gravity',
      'clean',
      'low-ph',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-dv10/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Cider and fruit — the strains Lallemand's cider pages single out
  // -------------------------------------------------------------------------
  {
    id: 'lalvin-71b',
    lab: 'Lalvin',
    productCode: '71B',
    name: 'Lalvin 71B',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      "An INRAE Narbonne strain that metabolises 20–40 % of the must's malic acid and pays it back in banana-and-pear amyl esters, so it rounds off a sharp cider or a hard-edged fruit wine. It also adsorbs some polyphenol on its cell wall, softening tannin. Tolerance stops at 14 %, and it needs oxygen early.",
    tags: [
      'wine',
      'cider',
      'mead',
      'fruit-wine',
      'esters',
      'fruity',
      'malic-acid',
      'low-acid',
      'nouveau',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-71b/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-icv-okay',
    lab: 'Lalvin',
    productCode: 'ICV OKAY',
    name: 'Lalvin ICV Okay',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 12, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'Bred by marker-assisted selection for one trick: it produces very little to no SO2, H2S or acetaldehyde. That makes it the strain for a cider or a low-sulfite wine that should smell of fruit and nothing else. Very short lag, steady rate, low nutrient demand and a wide 12–30 °C window.',
    tags: ['wine', 'cider', 'clean', 'low-sulfite', 'no-h2s', 'fruity', 'rose', 'white-wine'],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-icv-okay/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-sensy',
    lab: 'Lalvin',
    productCode: 'SENSY',
    name: 'Lalvin Sensy',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 12, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14.5,
    sta1: null,
    description:
      "A deliberately cold-run white-wine strain — 12–18 °C, and it stops at 14.5 % — bred alongside ICV Okay for minimal H2S and SO2 so that varietal aroma is not masked by sulfur. Lallemand's cider pages credit it with citrus and tropical fruit over a mineral edge, plus more mouthfeel than a cool ferment usually gives.",
    tags: [
      'wine',
      'cider',
      'cold-fermented',
      'low-sulfite',
      'no-h2s',
      'citrus',
      'tropical',
      'white-wine',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-sensy/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // White and rosé
  // -------------------------------------------------------------------------
  {
    id: 'lalvin-icv-d47',
    lab: 'Lalvin',
    productCode: 'ICV D47',
    name: 'Lalvin ICV D47',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 28 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A Côtes du Rhône white-wine isolate that releases a lot of polysaccharide, giving a round, silky palate, and has the β-glucosidase activity to pull citrus and floral terpenes out of the must. Left on the lees it turns tropical. Low foam and a compact sediment; below 15 °C in a clarified juice it sulks.',
    tags: [
      'wine',
      'mead',
      'white-wine',
      'chardonnay',
      'citrus',
      'floral',
      'mouthfeel',
      'polysaccharide',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-icv-d47/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-qa23',
    lab: 'Lalvin',
    productCode: 'QA23',
    name: 'Lalvin QA23',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 14, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A Vinho Verde selection with very high β-glucosidase activity, so it lifts lime and grapefruit aromas and converts thiols out of an otherwise plain base. Fructophilic, undemanding on nitrogen and content in a heavily clarified juice — which is why it survives the thin nutrient of a cider or a sugar wash.',
    tags: [
      'wine',
      'mead',
      'white-wine',
      'citrus',
      'thiols',
      'fructophilic',
      'low-nutrient',
      'clean',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-qa23/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-r2',
    lab: 'Lalvin',
    productCode: 'R2',
    name: 'Lalvin R2',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 5, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A Sauternes isolate with genuine cold tolerance — Lallemand put its floor at 5 °C — that makes neutral juice taste fruity by releasing terpenes and throwing esters. It is sensitive to poor nutrition and will answer a starved ferment with volatile acidity, so feed it and protect it at rehydration.',
    tags: [
      'wine',
      'white-wine',
      'cold-tolerant',
      'esters',
      'terpenes',
      'thiols',
      'aromatic',
      'sauvignon-blanc',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-r2/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-cy3079',
    lab: 'Lalvin',
    productCode: 'CY3079',
    name: 'Lalvin CY3079',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'The Burgundian barrel-Chardonnay reference: it ferments steadily and then slows to a crawl near the end, which is what you want for a long lees contact. Autolysing early, it gives butter, toasted bread, honey and hazelnut. Nitrogen demand is high — underfeed it and the last few points drag on for weeks.',
    tags: [
      'wine',
      'white-wine',
      'chardonnay',
      'lees',
      'buttery',
      'nutty',
      'mouthfeel',
      'high-nutrient',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-cy3079/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-ba11',
    lab: 'Lalvin',
    productCode: 'BA11',
    name: 'Lalvin BA11',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A Bairrada selection for white and rosé that reads as orange blossom, pineapple and apricot, with enough polysaccharide to put some weight on the palate. Kinetics stay good at the cool end of its 15–25 °C band and it barely foams; nutrient demand is on the high side.',
    tags: [
      'wine',
      'white-wine',
      'rose',
      'floral',
      'stone-fruit',
      'tropical',
      'mouthfeel',
      'cold-fermented',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-ba11/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-w15',
    lab: 'Lalvin',
    productCode: 'W15',
    name: 'Lalvin W15',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 10, max: 27 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A Müller-Thurgau isolate from Wädenswil that ferments slowly and cool, 10–27 °C, and generates very little heat of its own, which keeps sulfur compounds down. High glycerol and succinic acid give the wine structure, and its osmotolerance holds up in late-harvest and ice-wine sugar.',
    tags: [
      'wine',
      'mead',
      'cold-fermented',
      'glycerol',
      'structure',
      'osmotolerant',
      'ice-wine',
      'low-sulfur',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-w15/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Red
  // -------------------------------------------------------------------------
  {
    id: 'lalvin-rc212',
    lab: 'Lalvin',
    productCode: 'RC212',
    name: 'Lalvin RC212',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 18, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'The Burgundy Pinot noir reference strain, kept for the color it leaves alone: it adsorbs little polyphenol on its cell walls, so anthocyanin and structure survive into the glass. Ripe cherry, bright fruit and spice. Wants 18–30 °C and average nutrition, and produces no foam at all.',
    tags: [
      'wine',
      'mead',
      'red-wine',
      'pinot-noir',
      'cherry',
      'spicy',
      'colour-stable',
      'structure',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-rc212/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-icv-d254',
    lab: 'Lalvin',
    productCode: 'ICV D254',
    name: 'Lalvin ICV D254',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A Syrah isolate from the Costières de Nîmes that makes a great deal of polysaccharide: prune and blackberry across a wide mid-palate, smooth tannin and a mildly spicy finish. In Chardonnay it turns butterscotch and nut instead. Hold it under 28 °C and aerate if you want the full 16 %.',
    tags: [
      'wine',
      'red-wine',
      'dark-fruit',
      'spicy',
      'mouthfeel',
      'polysaccharide',
      'tannin',
      'colour-stable',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-icv-d254/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-bm-4x4',
    lab: 'Lalvin',
    productCode: 'BM4x4',
    name: 'Lalvin BM4x4',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      "Lallemand's rebuild of BM45 for fermentation security: the same heavy polysaccharide release, round mouthfeel and color stability, but it actually finishes. Wants 18–28 °C and generous nutrition, makes moderate to high SO2, and is no friend to a malolactic culture.",
    tags: [
      'wine',
      'red-wine',
      'mouthfeel',
      'polysaccharide',
      'colour-stable',
      'tannin',
      'high-nutrient',
    ],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-bm-4x4/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-rhone-2056',
    lab: 'Lalvin',
    productCode: 'Rhône 2056',
    name: 'Lalvin Rhône 2056',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 28 },
    flocculation: 'medium',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'Picked by Inter-Rhône out of 1500 strains for β-damascenone — the floral compound that makes fruit read riper than it is — along with strong color extraction and high glycerol. Fast and short-lagged across 15–28 °C, moderate on volatile acidity, SO2 and H2S alike.',
    tags: ['wine', 'red-wine', 'rose', 'floral', 'red-fruit', 'spicy', 'glycerol', 'colour-stable'],
    url: 'https://www.lallemandwine.com/en/australia/products/wine-yeasts/lalvin-rhone-2056/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'lalvin-icv-gre',
    lab: 'Lalvin',
    productCode: 'ICV GRE',
    name: 'Lalvin ICV GRE',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 95, max: 100 },
    temperatureC: { min: 15, max: 30 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A Cornas isolate that starts quickly and then holds a steady rate, pushing spice, strawberry and confectionery esters forward. On a short skin contact it scrubs vegetal and sulfur notes out of red must. Medium nutrient demand, high glycerol, and it likes some oxygen about a third of the way down.',
    tags: ['wine', 'red-wine', 'rose', 'esters', 'red-fruit', 'spicy', 'glycerol', 'fruit-forward'],
    url: 'https://www.lallemandwine.com/en/united-states/products/wine-yeasts/lalvin-icv-gre/',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
];
